import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Music, ThumbsUp, Send, Lock, Search, Loader2, PartyPopper, Calendar } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${API}/api`;

export default function GuestRequestsPage() {
  const { linkCode } = useParams();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [guestName, setGuestName] = useState('');
  const [eventInfo, setEventInfo] = useState(null);
  
  // Request form
  const [songTitle, setSongTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Songs list
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);

  const handleLogin = async () => {
    if (!password.trim() || !guestName.trim()) {
      toast.error('Please enter your name and the event password');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE}/guest-requests/${linkCode}/login`, {
        password: password,
        guest_name: guestName
      });
      
      setEventInfo(response.data);
      setIsLoggedIn(true);
      localStorage.setItem(`guest_${linkCode}`, JSON.stringify({ password, guestName }));
      fetchRequests();
      toast.success(`Welcome, ${guestName}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const fetchRequests = async () => {
    setLoadingRequests(true);
    try {
      const response = await axios.get(`${API_BASE}/guest-requests/${linkCode}/songs?password=${encodeURIComponent(password)}`);
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleSubmitRequest = async () => {
    if (!songTitle.trim() || !artist.trim()) {
      toast.error('Please enter both song title and artist');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/guest-requests/${linkCode}/submit?password=${encodeURIComponent(password)}`, {
        guest_name: guestName,
        song_title: songTitle,
        artist: artist,
        notes: notes
      });
      
      toast.success('Song request submitted!');
      setSongTitle('');
      setArtist('');
      setNotes('');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpvote = async (requestId) => {
    try {
      const response = await axios.post(
        `${API_BASE}/guest-requests/${linkCode}/upvote/${requestId}?guest_name=${encodeURIComponent(guestName)}&password=${encodeURIComponent(password)}`
      );
      
      // Update local state
      setRequests(prev => prev.map(req => {
        if (req.id === requestId) {
          const newUpvotes = response.data.action === 'added' 
            ? [...(req.upvotes || []), guestName]
            : (req.upvotes || []).filter(n => n !== guestName);
          return { ...req, upvotes: newUpvotes, upvote_count: response.data.new_count };
        }
        return req;
      }));
    } catch (error) {
      toast.error('Failed to upvote');
    }
  };

  // Check for saved session
  useEffect(() => {
    const saved = localStorage.getItem(`guest_${linkCode}`);
    if (saved) {
      try {
        const { password: savedPw, guestName: savedName } = JSON.parse(saved);
        setPassword(savedPw);
        setGuestName(savedName);
        // Auto-login with saved credentials
        (async () => {
          try {
            const response = await axios.post(`${API_BASE}/guest-requests/${linkCode}/login`, {
              password: savedPw,
              guest_name: savedName
            });
            setEventInfo(response.data);
            setIsLoggedIn(true);
            // Fetch requests
            const reqResponse = await axios.get(`${API_BASE}/guest-requests/${linkCode}/songs?password=${encodeURIComponent(savedPw)}`);
            setRequests(reqResponse.data);
          } catch {
            localStorage.removeItem(`guest_${linkCode}`);
          }
        })();
      } catch {
        localStorage.removeItem(`guest_${linkCode}`);
      }
    }
  }, [linkCode]);

  const primaryColor = eventInfo?.primary_color || '#00d4ff';

  // Login screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center p-4">
        <Toaster richColors position="top-center" />
        
        <div className="w-full max-w-md">
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 p-8 shadow-2xl">
            <div className="text-center mb-8">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                <Music className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">Guest Song Requests</h1>
              <p className="text-white/60">Request songs for the event playlist!</p>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white">Your Name *</Label>
                <Input
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder="Enter your name"
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  data-testid="guest-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-white flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Event Password *
                </Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password from host"
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                  data-testid="guest-password-input"
                />
              </div>
              
              <Button
                onClick={handleLogin}
                disabled={loading}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white h-12"
                data-testid="guest-login-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Joining...
                  </>
                ) : (
                  <>
                    <PartyPopper className="w-4 h-4 mr-2" />
                    Join & Request Songs
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main request interface
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      <Toaster richColors position="top-center" />
      
      {/* Header */}
      <div className="bg-black/30 backdrop-blur-xl border-b border-white/10 p-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {eventInfo?.logo_url ? (
              <img src={eventInfo.logo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Music className="w-5 h-5 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-white font-semibold">{eventInfo?.client_name}'s {eventInfo?.event_type}</h1>
              <p className="text-white/50 text-sm flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {eventInfo?.event_date}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-white/80 text-sm">Welcome, <span className="font-medium">{guestName}</span>!</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-6">
        {/* Request Form */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-6">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Send className="w-5 h-5" style={{ color: primaryColor }} />
            Request a Song
          </h2>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Song Title *</Label>
                <Input
                  value={songTitle}
                  onChange={(e) => setSongTitle(e.target.value)}
                  placeholder="Enter song title"
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  data-testid="song-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Artist *</Label>
                <Input
                  value={artist}
                  onChange={(e) => setArtist(e.target.value)}
                  placeholder="Enter artist name"
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  data-testid="song-artist-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-white">Notes (optional)</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g., Play during dinner, for the bride's dad, etc."
                className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                data-testid="song-notes-input"
              />
            </div>
            
            <Button
              onClick={handleSubmitRequest}
              disabled={submitting || !songTitle.trim() || !artist.trim()}
              className="w-full text-white h-11"
              style={{ backgroundColor: primaryColor }}
              data-testid="submit-request-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Music className="w-4 h-4 mr-2" />
                  Submit Request
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Requested Songs */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Music className="w-5 h-5" style={{ color: primaryColor }} />
              Requested Songs
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchRequests}
              disabled={loadingRequests}
              className="text-white/60 hover:text-white"
            >
              {loadingRequests ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>
          
          <p className="text-white/50 text-sm mb-4">
            See what others have requested. Upvote songs you'd love to hear!
          </p>
          
          {requests.length === 0 ? (
            <div className="text-center py-8">
              <Music className="w-12 h-12 text-white/30 mx-auto mb-3" />
              <p className="text-white/50">No requests yet. Be the first!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {requests.map((req) => {
                const hasUpvoted = (req.upvotes || []).includes(guestName);
                const isApproved = req.status === 'approved';
                
                return (
                  <div
                    key={req.id}
                    className={`flex items-center justify-between p-4 rounded-xl transition-all ${
                      isApproved 
                        ? 'bg-green-500/20 border border-green-500/30' 
                        : 'bg-white/5 border border-white/10 hover:bg-white/10'
                    }`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-white font-medium">{req.song_title}</p>
                        {isApproved && (
                          <span className="text-xs bg-green-500/30 text-green-300 px-2 py-0.5 rounded">
                            Added to playlist!
                          </span>
                        )}
                      </div>
                      <p className="text-white/60 text-sm">{req.artist}</p>
                      <p className="text-white/40 text-xs mt-1">
                        Requested by {req.guest_name}
                        {req.notes && <span className="ml-2 italic">"{req.notes}"</span>}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUpvote(req.id)}
                        className={`flex items-center gap-1 ${
                          hasUpvoted 
                            ? 'text-pink-400 hover:text-pink-300' 
                            : 'text-white/50 hover:text-white'
                        }`}
                        data-testid={`upvote-btn-${req.id}`}
                      >
                        <ThumbsUp className={`w-4 h-4 ${hasUpvoted ? 'fill-current' : ''}`} />
                        <span className="text-sm font-medium">{req.upvote_count || 0}</span>
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="text-center py-4">
          <p className="text-white/30 text-xs">
            {eventInfo?.company_name && `DJ services by ${eventInfo.company_name}`}
          </p>
        </div>
      </div>
    </div>
  );
}
